import { Component, Prop } from 'vue-property-decorator';
import { Application, ApplicationOptions } from 'pixi.js';
import { AssetManager } from '@arcade-client/asset-manager';
import { HowToPlayAnimations, HowToPlaySlide } from '@/components/arcade-game/_components/how-to-play/_domain/animations';
import { IHtpConfigurationService, IHtpGameConfiguration, IHtpSlideControlTextures } from '@/components/arcade-game/_components/how-to-play/_domain/modules';
import TombolaBaseComponent from '@/components/base/base.component';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { UserPreferences, ColourTheme } from '@/app-modules/store/modules/user-preferences';
import { UserSettingsUpdatedEventName } from '@/components/user-settings/_domain/models/events';

@Component({
  name: 'HowToPlayComponent'
})
export default class HowToPlayComponent extends TombolaBaseComponent {
  @Prop() public gameName: string;
  private app: Application;
  private howToPlayAnimations: HowToPlayAnimations;

  private get howToPlayConfigurationService(): IHtpConfigurationService {
    return this.$container.get(SERVICE_IDENTIFIERS.IHtpConfigurationService);
  }

  private async mounted() {
    await this.initialisePixiAppConfiguration();
    this.initialisePixiApp();
    this.initialiseUserSettingsEventHandler();
  }

  private beforeDestroy() {
    this.destroyUserSettingsEventHandler();
  }

  private async initialisePixiAppConfiguration(): Promise<void> {
    const view = this.$refs.howToPlay as HTMLCanvasElement;
    const htpConfig = await this.getHtpConfiguration();
    const htpSlideConfiguration = await this.howToPlayConfigurationService.getSlideControlAssetNames();
    await this.loadHtpAssets();
    this.howToPlayAnimations = this.getHtpAnimations(htpConfig, htpSlideConfiguration, view);
  }

  private initialisePixiApp() {
    const view = this.$refs.howToPlay as HTMLCanvasElement;
    const htpContainer = this.$refs.htpContainer as HTMLElement;
    const pixiOptions = this.getPixiOptions(view, htpContainer);
    this.app = new Application(pixiOptions);
    this.app.stage.addChild(this.howToPlayAnimations);
    this.app.ticker.speed = 0.5; // Sets the ticker to run at 30fps max
    this.app.ticker.start();
    this.app.render();
  }

  private getPixiOptions(view: HTMLCanvasElement, htpContainer: HTMLElement): ApplicationOptions {
    const userPreferences = this.$tombolaStore.Get(UserPreferences);
    const colourTheme = userPreferences.colourTheme;

    return {
      backgroundColor: colourTheme === ColourTheme.Light ? 0xffffff : 0x444444,
      forceCanvas: true,
      preserveDrawingBuffer: true,
      width: htpContainer.clientWidth,
      height: htpContainer.clientHeight,
      view,
      resolution: window.devicePixelRatio
    };
  }

  private async getHtpConfiguration(): Promise<IHtpGameConfiguration> {
    return await this.howToPlayConfigurationService.getConfigurationForGame(this.gameName);
  }

  private async loadHtpAssets(): Promise<void> {
    await this.howToPlayConfigurationService.loadHtpAssetsForGame(this.gameName);
  }

  private getHtpAnimations(htpConfig: IHtpGameConfiguration, htpSlideConfiguration: IHtpSlideControlTextures, view: HTMLCanvasElement): HowToPlayAnimations {
    const dimensions = {
      width: view.clientWidth,
      height: view.clientHeight
    };
    return new HowToPlayAnimations(
      this.getAnimationSlides(htpConfig),
      this.getSlideControlAssets(htpSlideConfiguration),
      dimensions
    );
  }

  private getAnimationSlides(htpConfig: IHtpGameConfiguration): HowToPlaySlide[] {
    const slideNames = htpConfig.assetList.animations.map((animation) => animation.name);
    const assetManagerResources = AssetManager.loader.resources;
    return slideNames.map((slideName) => new HowToPlaySlide(assetManagerResources[slideName].spineData));
  }

  private getSlideControlAssets(htpSlideConfiguration: IHtpSlideControlTextures): { enabled: PIXI.Texture, disabled: PIXI.Texture } {
    const resources = AssetManager.loader.resources;
    return {
      enabled: resources[htpSlideConfiguration.enabled.name].texture,
      disabled: resources[htpSlideConfiguration.disabled.name].texture
    };
  }

  private onUserSettingsChanged(): void {
    this.initialisePixiApp();
  }

  private initialiseUserSettingsEventHandler(): void {
    this.$eventbus.$on(UserSettingsUpdatedEventName, this.onUserSettingsChanged);
  }

  private destroyUserSettingsEventHandler(): void {
    this.$eventbus.$off(UserSettingsUpdatedEventName, this.onUserSettingsChanged);
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/arcade-game/_components/how-to-play/how-to-play.component.ts